import { LoadingButton } from "@mui/lab";
import {
  Alert, Button, Dialog, DialogActions, DialogContent, DialogTitle,
  LinearProgress, Stack, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Typography,
} from "@mui/material";
import { useEffect, useRef, useState } from "react";
import { evaluation_refonte_reponse_service as evaluations } from "../../../../services/PersonalEvaluationRefonteService";

export default function ValidateEvaluationDialog({ employee, onClose, onValidated }) {
  const [snapshot, setSnapshot] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [attempt, setAttempt] = useState(0);
  const submitting = useRef(false);

  useEffect(() => {
    const controller = new AbortController();
    setLoading(true);
    setError("");
    setSnapshot(null);
    evaluations.getLastEvaluationPreview(employee.user_id, controller.signal)
      .then((data) => { if (!controller.signal.aborted) setSnapshot(data); })
      .catch((failure) => {
        if (!controller.signal.aborted) setError(failure.response?.data?.message || failure.message);
      })
      .finally(() => { if (!controller.signal.aborted) setLoading(false); });
    return () => controller.abort();
  }, [employee.user_id, attempt]);

  const confirm = async () => {
    if (submitting.current || !snapshot) return;
    submitting.current = true;
    setSaving(true);
    setError("");
    try {
      const result = await evaluations.duplicateLastEvaluation(
        employee.user_id, snapshot.employee.ID_SAISON, snapshot.source_version,
      );
      if (!result?.success) throw new Error(result?.message || "Impossible de valider l'évaluation.");
      onValidated(result.message);
    } catch (failure) {
      setError(failure.response?.data?.message || failure.message);
      // A conflict or uncertain network result requires a fresh preview before retrying.
      setSnapshot(null);
    } finally {
      submitting.current = false;
      setSaving(false);
    }
  };

  const source = snapshot?.employee;
  return <Dialog open fullWidth maxWidth="lg" onClose={saving ? undefined : onClose}
    aria-labelledby="validate-evaluation-title">
    <DialogTitle id="validate-evaluation-title">Vérifier l’évaluation de {employee.candidat_name}</DialogTitle>
    <DialogContent dividers>
      {loading && <LinearProgress aria-label="Chargement de la dernière évaluation" />}
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {!loading && !snapshot && <Button onClick={() => setAttempt((value) => value + 1)}>Recharger l’évaluation</Button>}
      {snapshot && <Stack spacing={2}>
        <Alert severity="info">En confirmant, vous reprenez ces réponses, notes et commentaires à l’identique.
          Vous serez enregistré comme évaluateur de cette nouvelle évaluation envoyée.</Alert>
        <Typography>Dernier évaluateur : {source.dernier_evaluateur || "—"} · Date : {source.DATE_EVALUATION || "—"}</Typography>
        <Typography>Note générale : {source.NOTE_GENERALE ?? "—"} / {source.NOTE_MAXI ?? "—"} · {source.appreciation}</Typography>
        <TableContainer>
          <Table size="small" aria-label="Détails de la dernière évaluation">
            <TableHead><TableRow>
              <TableCell>Section</TableCell><TableCell>Question</TableCell>
              <TableCell>Note</TableCell><TableCell>Réponse</TableCell><TableCell>Commentaire</TableCell>
            </TableRow></TableHead>
            <TableBody>{snapshot.answers.map((answer) => <TableRow key={answer.ID_REPONSE}>
              <TableCell>{answer.CATEGORY_NAME ?? "—"}</TableCell>
              <TableCell>{answer.QUESTION ?? `Question ${answer.ID_QUESTIONNAIRE}`}</TableCell>
              <TableCell>{answer.NOTE == null ? "—" : `${answer.NOTE} / ${answer.NOTE_MAX ?? "—"}`}</TableCell>
              <TableCell sx={{ whiteSpace: "pre-wrap" }}>{answer.REPONSE ?? "—"}</TableCell>
              <TableCell sx={{ whiteSpace: "pre-wrap" }}>{answer.COMMENTAIRE_SUP ?? "—"}</TableCell>
            </TableRow>)}</TableBody>
          </Table>
        </TableContainer>
      </Stack>}
    </DialogContent>
    <DialogActions>
      <Button onClick={onClose} disabled={saving}>Annuler</Button>
      <LoadingButton variant="contained" onClick={confirm} loading={saving}
        disabled={loading || !snapshot?.answers?.length}>Confirmer la validation</LoadingButton>
    </DialogActions>
  </Dialog>;
}
